<template>
  <f7-block>
    <DxForm>
      <DxGroupItem :col-count="2">
        <DxSimpleItem
          :col-span="1"
          :data-field="$t('layerOptions_select_index')"
          :caption="$t('layerOptions_select_index')"
          editor-type="dxSelectBox"
          :editor-options="{
            items: indexAvailables,
            value: currentIndexes[`${keyId}`],
            placeholder: $t('layerOptions_index_availables'),
            onValueChanged: onSelectedIndex,
          }"
        >
          <DxRequiredRule :message="$t('DxRegisterCampania_end_date_require')" />
        </DxSimpleItem>

        <DxSimpleItem>
          <template #default>
            <div style="display: flex; gap: 1rem;">
              <f7-input
                v-if="enableDatePicker"
                type="datepicker"
                class="recepDate recep-date-picker"
                :placeholder="$t('PlanningComponent_datepicker_multiple_placeholder')"
                :clear-button="true"
                :close-on-select="true"
                :value="layerOptionsDates"
                :calendar-params="{
                  closeOnSelect: true,
                  rangePicker: true,
                  dateFormat: {
                    month: 'numeric',
                    day: 'numeric',
                    year: 'numeric',
                  },
                }"
                @calendar:change="setLayerOptionsDates"
              />
              <div>
                <ContextMenu :items="items" />
              </div>
            </div>
          </template>
        </DxSimpleItem>

        <DxSimpleItem
          v-if="!enableDatePicker"
          name="init-date"
          :data-field="$t('DxRegisterCampania_fecha_inicio')"
          editor-type="dxDateBox"
          :editor-options="{
            value: actualInitDate,
            onValueChanged: onChangeInitDate,
            displayFormat: 'dd/MM/yyyy',
            placeholder: $t('registerClima_initDate_placeholder'),
            invalidDateMessage: $t('dates_isInvalid'),
            pickerType: 'calendar',
          }"
        />
        <DxSimpleItem
          v-if="!enableDatePicker"
          name="end-date"
          :data-field="$t('DxRegisterCampania_fecha_finalizacion')"
          editor-type="dxDateBox"
          :editor-options="{
            value: actualEndDate,
            onValueChanged: onChangeEndDate,
            displayFormat: 'dd/MM/yyyy',
            placeholder: $t('registerClima_endDate_placeholder'),
            invalidDateMessage: $t('dates_isInvalid'),
            pickerType: 'calendar',
          }"
        />
      </DxGroupItem>
    </DxForm>
  </f7-block>
</template>

<script>
import {
  DxForm,
  DxSimpleItem,
  DxGroupItem,
  DxRequiredRule,
} from 'devextreme-vue/form';

import { mapActions, mapState } from 'vuex';
import moment from 'moment';
import ContextMenu from '@/components/contextMenu/index.vue';
import EventBus from '../../../js/event-bus';
import { applyWidenedTimelineRange, timelineRangeWidenedEvent } from '../../../js/utils/gisDateRange';

export default {
  name: 'LayerOptions',
  components: {
    DxForm,
    DxSimpleItem,
    DxGroupItem,
    DxRequiredRule,
    ContextMenu,
  },
  props: {
    keyId: {
      type: String,
      default: 'layerOptions',
    },
    enableDatePicker: {
      type: Boolean,
      default: false,
    },
    defaultIndex: {
      type: String,
      default: 'NDVI',
    },
  },
  data() {
    return {
      actualInitDate: '',
      actualEndDate: moment().format('YYYY-MM-DD'),
      items: [
        {
          icon: 'download',
          text: this.$t('ContextMenu.Download'),
          onItemClick: this.viewReport,
        },
      ],
    };
  },
  computed: {
    layerOptionsDates() {
      if (
        this.actualInitDate !== null
        && this.actualEndDate !== null
      ) {
        return [this.actualInitDate, this.actualEndDate];
      }
      return [];
    },
    ...mapState('Gis', ['indexAvailables', 'currentIndexes', 'currentInitIntervalDates', 'currentEndIntervalDates', 'currentOverlayLayer', 'timelineIndexes']),
    ...mapState('Plantation', ['currentPlantation']),
  },
  beforeMount() {
    this.setCurrentIndexes({
      key: this.keyId,
      newCurrentIndex: this.defaultIndex,
    });
    // El rango por defecto (último mes) lo fija el padre (GisTimeDimension) en el
    // store antes de montar este componente — lo leemos de ahí para que el texto
    // del selector no se desincronice de lo que realmente se está filtrando.
    // currentPlantation.initDate como fallback por si este componente se usa sin
    // ese padre (mismo comportamiento que antes en ese caso).
    this.actualInitDate = this.currentInitIntervalDates[this.keyId]
      ? moment(this.currentInitIntervalDates[this.keyId])
      : moment(this.currentPlantation.initDate);
    if (this.currentEndIntervalDates[this.keyId]) {
      this.actualEndDate = moment(this.currentEndIntervalDates[this.keyId]).format('YYYY-MM-DD');
    }
  },
  mounted() {
    EventBus.$on(timelineRangeWidenedEvent(this.keyId), this.showWidenedRange);
  },
  beforeDestroy() {
    EventBus.$off(timelineRangeWidenedEvent(this.keyId), this.showWidenedRange);
  },
  methods: {
    /**
     * Refleja en el selector el rango ampliado con el que se ha cargado la línea temporal
     * (ver applyWidenedTimelineRange). El store ya está actualizado.
     */
    showWidenedRange({ initDate }) {
      this.actualInitDate = moment(initDate);
    },
    setLayerOptionsDates(e) {
      if (e.length === 2) {
        // Si es el rango ya aplicado (p. ej. el selector reflejando un rango ampliado), no
        // se vuelve a cargar nada
        if (moment(e[0]).isSame(this.currentInitIntervalDates[this.keyId], 'day')
          && moment(e[1]).isSame(this.currentEndIntervalDates[this.keyId], 'day')) {
          return;
        }
        this.actualInitDate = e[0];
        this.actualEndDate = e[1];

        const dateInit = moment(this.actualInitDate, 'YYYY/MM/DD').format('YYYY-MM-DDT00:00:00');
        this.setCurrentInitIntervalDates({
          key: this.keyId,
          newCurrentInitIntervalDate: dateInit,
        });
        const dateEnd = moment(this.actualEndDate, 'YYYY/MM/DD').format('YYYY-MM-DDT23:59:59');
        this.setCurrentEndIntervalDates({
          key: this.keyId,
          newCurrentEndIntervalDate: dateEnd,
        });

        EventBus.$emit(`updateTimeDimension${this.keyId}`);
        EventBus.$emit('filteringTasks');

        this.callToFetchTimelineIndexes(false);
      } else if (e.length === 0) {
        this.actualInitDate = null;
        this.actualEndDate = null;
      }
    },
    onChangeInitDate(e) {
      let date;
      if (e.value === null) {
        date = '1970/01/01';
      } else {
        date = e.value;
      }
      const dateInit = moment(date, 'YYYY/MM/DD').format('YYYY-MM-DDT00:00:00');
      this.setCurrentInitIntervalDates({
        key: this.keyId,
        newCurrentInitIntervalDate: dateInit,
      });
      EventBus.$emit(`updateTimeDimension${this.keyId}`);
      EventBus.$emit('filteringTasks');
      this.callToFetchTimelineIndexes();
    },
    onChangeEndDate(e) {
      let date;
      if (e.value === null) {
        date = '3000/01/01';
      } else {
        date = e.value;
      }
      const dateEnd = moment(date, 'YYYY/MM/DD').format('YYYY-MM-DDT23:59:59');
      this.setCurrentEndIntervalDates({
        key: this.keyId,
        newCurrentEndIntervalDate: dateEnd,
      });
      EventBus.$emit(`updateTimeDimension${this.keyId}`);
      EventBus.$emit('filteringTasks');
      this.callToFetchTimelineIndexes(false);
    },
    onSelectedIndex(e) {
      this.setCurrentIndexes({
        key: this.keyId,
        newCurrentIndex: e.value,
      });
      EventBus.$emit(`indexLayer${this.keyId}`, e.value);
      this.callToFetchTimelineIndexes(true);
    },
    async callToFetchTimelineIndexes(indexChanged) {
      if (indexChanged) {
        const currentTimeLineIndexes = this.timelineIndexes;
        this.setTimelineIndexes(null);
        this.setTimelineIndexes(currentTimeLineIndexes);
        return;
      }

      try {
        this.setTimelineIndexes(null);
        const result = await this.fetchTimelineIndexes({
          location: this.currentPlantation.sectorId,
          initDate: this.currentInitIntervalDates.GisTimeDimension !== '' ? moment(this.currentInitIntervalDates.GisTimeDimension).format('YYYY-MM-DD') : moment().format('YYYY-MM-DD'),
          endDate: this.currentEndIntervalDates.GisTimeDimension !== '' ? moment(this.currentEndIntervalDates.GisTimeDimension).format('YYYY-MM-DD') : moment().format('YYYY-MM-DD'),
          companyId: this.currentPlantation.companyId,
          // Mismo criterio que el mapa: si el rango no tiene imágenes, se amplía
          widenRangeIfEmpty: true,
        });
        applyWidenedTimelineRange(this, this.keyId, result);
      } catch (error) {
        this.$notifyDX(
          {
            message: this.$t(`${error}`),
            width: 550,
          },
          'error',
          3000,
        );
      }
    },
    viewReport() {
      this.initDataReportGis();
      this.setInitDate(this.currentInitIntervalDates.GisTimeDimension);
      this.setEndDate(this.currentEndIntervalDates.GisTimeDimension);

      this.$f7.views.main.router.navigate('/reportGis/', { reloadCurrent: true });
    },
    ...mapActions('Gis', ['setCurrentIndexes', 'setCurrentInitIntervalDates', 'setCurrentEndIntervalDates', 'fetchTimelineIndexes', 'setTimelineIndexes']),
    ...mapActions('ReportGis', ['initDataReportGis', 'setInitDate', 'setEndDate']),
  },
};
</script>

<style lang="scss" scoped>
@import './LayerOptions.styles.scss';
</style>
